'use client';

import { type Context, createContext, use } from 'react';

// ── Stage discriminated union ────────────────────────────────────────────────
//
// Resolution is atomic on chain (resolve, drain the losers and open claims in
// one transaction), so the pool is either still accumulating or settled. The
// `finalized-non-holder` case folds into `'finalized-loser'` (same copy).

export type PrizePoolStage = 'loading' | 'pre-finalization' | 'finalized-winner' | 'finalized-loser';

// ── Context shape ────────────────────────────────────────────────────────────

export interface PrizePoolPayoutEstimateView {
  readonly outcomeIndex: number;
  readonly label: string;
  readonly claimantShareMicroPercent: number;
  readonly estimatedPayoutD6: string;
}
export interface PrizePoolState {
  stage: PrizePoolStage;
  locked: boolean;
  amount: number;
  userShareD6: string | null;
  userSharePct: number;
  payoutEstimates: readonly PrizePoolPayoutEstimateView[] | null;
  payoutEstimatesPending: boolean;
  /**
   * Label of the winning outcome (e.g. "Yes") — surfaced in the finalized-loser
   * card as a closure chip. `null` when no winner has been resolved yet.
   */
  winningOutcomeLabel: string | null;
  claimed: boolean;
  /**
   * True while a claim transaction is in flight. While true, the claim button
   * renders a spinner and is disabled — matches the legacy behaviour where
   * both `showClaimed` and `showClaimButton` were false during the tx.
   */
  claimInFlight: boolean;
  helpModalOpen: boolean;
}
export interface PrizePoolActions {
  onClaim: () => void;
  setHelpModalOpen: (open: boolean) => void;
}
export interface PrizePoolContextValue {
  state: PrizePoolState;
  actions: PrizePoolActions;
}
export const PrizePoolContext: Context<PrizePoolContextValue | null> = createContext<PrizePoolContextValue | null>(null);

/**
 * Hook to read the PrizePool context. Throws when called outside a
 * `<PrizePool.Provider>` so consumer-error is loud, not silent.
 */
export function usePrizePoolContext(): PrizePoolContextValue {
  const ctx = use(PrizePoolContext);
  if (ctx === null) {
    throw new Error('usePrizePoolContext must be used within a PrizePool.Provider');
  }
  return ctx;
}